import type React from "react";
import { useTranslation } from "react-i18next";
import { MappingStatus } from "@models/job";
import { ConfidenceBand, MappingResultKind } from "@models/mappingResult";
import type {
  MappingResultRow,
  MappingResultTarget,
} from "@models/mappingResult";
import { LOW_CONFIDENCE_BADGE, MAPPING_STATUS_BADGE, tmNoMatchBadge } from "@utils/resultStatusBadges";
import StatusBadge from "./StatusBadge";

interface RowFindingBadgeProps {
  row: MappingResultRow;
  /** The target rendered beside this badge, when there is one. Only read to
   *  decide whether the finding is already said by the AI marker over it. */
  target?: MappingResultTarget | null;
}

/**
 * Display machine findings beside the proposed value/item, independently
 * from review status. The shared `no_match` verdict receives a TM-value
 * or WD-item label; a WD low-confidence `matched` row gets its own warning.
 * A WD AI proposal already communicates its missing catalogue item.
 */
const RowFindingBadge: React.FC<RowFindingBadgeProps> = ({ row, target }) => {
  const { t } = useTranslation();
  const spec = row.mappingStatus === MappingStatus.NoMatch && row.kind === MappingResultKind.Tm
    ? tmNoMatchBadge(row.reason)
    : row.mappingStatus === MappingStatus.Matched && row.kind === MappingResultKind.Oe &&
        target != null && "confidenceBand" in target &&
        target.confidenceBand === ConfidenceBand.Low
      ? LOW_CONFIDENCE_BADGE
      : MAPPING_STATUS_BADGE[row.mappingStatus];
  if (!spec) return null;
  if (
    row.mappingStatus === MappingStatus.NoMatch &&
    target != null &&
    "isAiSuggestion" in target &&
    target.isAiSuggestion
  ) {
    return null;
  }

  return (
    <StatusBadge
      size="sm"
      spec={spec}
      text={t(spec.badgeKey, { defaultValue: spec.badge })}
      label={t(spec.labelKey, { defaultValue: spec.fallback })}
    />
  );
};

export default RowFindingBadge;
